import { PushpinFilled, PushpinOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Divider, Flex, Input, Pagination, Popconfirm, Select, Space, Tag, Tooltip, Typography } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Flag from "react-world-flags";
import { useAtom } from "jotai";
import { useNavigate } from "react-router-dom";
import { MutateData } from "../handlers/axios";
import { Defaults, Language, QueryCase, QueryId, WebRoutes } from "../handlers/enums";
import { BookId, Loading, SearchBooks, UserId } from "../handlers/globals";
import ComponentState from "./components/states";
import ComponentBookIcon from "./components/bookicon";

export function SectionSearch() {
    const { t } = useTranslation();

    const [userId] = useAtom(UserId);
    const [searchBooks, setSearchBooks] = useAtom(SearchBooks);
    const [, setLoading] = useAtom(Loading);

    const [searchInput, setSearchInput] = useState(Defaults.StringEmpty);
    const [freezeActions, setFreezeActions] = useState(false);
    const [page, setPage] = useState(1);
    const [language, setLanguage] = useState(Language.English);

    const emptySearchResult = {
        booksLite: [],
        totalItems: 0,
        hiddenOnPage: 0
    };

    const toApiPageIndex = (uiPage) => Math.max((uiPage ?? 1) - 1, 0);

    const actionSearch = MutateData(QueryId.SelectGoogleBooks, () => {
        setFreezeActions(false);
        setLoading(false);
    }, [
        {
            case: QueryCase.Success,
            onCase: (data) => {
                window.scrollTo(0, 0);
                setSearchBooks(data.data ?? emptySearchResult);
            }
        },
        {
            case: QueryCase.Empty,
            onCase: () => {
                setSearchBooks(emptySearchResult);
            }
        }
    ]);

    const onSearch = (uiPage = 1) => {
        if (!searchInput) {
            return;
        }

        setLoading(true);
        setFreezeActions(true);

        actionSearch.mutate({
            UserId: userId,
            SearchInput: searchInput,
            LanguageRestriction: language,
            PageIndex: toApiPageIndex(uiPage)
        });
    };

    const list = searchBooks?.booksLite ?? [];
    const discoverBooks = list.filter((book) => book.isDiscover);
    const regularBooks = list.filter((book) => !book.isDiscover);

    return (
        <Space direction="vertical" size={14} className="w-full">
            <Flex gap={8} align="center" wrap>
                <Select
                    value={language}
                    onChange={(value) => setLanguage(value)}
                    disabled={freezeActions}
                    className="app-select min-w-[120px]"
                    popupClassName="app-select-dropdown"
                    options={[
                        { value: Language.English, label: <Space><Flag width={22} code={Language.EnglishAlt} />EN</Space> },
                        { value: Language.Dutch, label: <Space><Flag width={22} code={Language.Dutch} />NL</Space> }
                    ]}
                />
                <Input
                    size="large"
                    value={searchInput}
                    className="app-input rounded-full custom-color-placeholder flex-1 min-w-[260px]"
                    onChange={(value) => setSearchInput(value.target.value)}
                    disabled={freezeActions}
                    placeholder={t("placeholder_searchbooks")}
                    allowClear
                    onPressEnter={() => {
                        setPage(1);
                        onSearch(1);
                    }}
                    addonAfter={<SearchOutlined onClick={() => {
                        setPage(1);
                        onSearch(1);
                    }} />}
                />
            </Flex>

            {searchBooks?.hiddenOnPage ? (
                <Flex justify="center">
                    <Tooltip placement="bottom" title={t("tooltip_hidden_books")}>
                        <Tag color="processing">
                            {searchBooks.hiddenOnPage} {searchBooks.hiddenOnPage === 1 ? t("book_hidden") : t("books_hidden")}
                        </Tag>
                    </Tooltip>
                </Flex>
            ) : null}

            <Pagination
                className="simple_pagination flex justify-center"
                onChange={(value) => {
                    if (value !== page) {
                        setPage(value);
                        onSearch(value);
                    }
                }}
                pageSize={20}
                current={page}
                total={searchBooks?.totalItems ?? 0}
                hideOnSinglePage
            />

            <Divider className="!my-1 !border-custom-darkgrey" />

            {regularBooks.length === 0 ? (
                <ComponentState empty description={t("search_filter_required")} />
            ) : (
                regularBooks.map((book) => <Book key={book.bookId} book={book} />)
            )}

            <Pagination
                className="simple_pagination flex justify-center"
                hideOnSinglePage
                onChange={(value) => {
                    if (value !== page) {
                        setPage(value);
                        onSearch(value);
                    }
                }}
                pageSize={20}
                current={page}
                total={searchBooks?.totalItems ?? 0}
            />

            {discoverBooks.length > 0 ? (
                <>
                    <Divider className="!my-1 !border-custom-darkgrey" />
                    <Typography.Title level={4} className="!mb-2 !text-center !text-custom-white">
                        {t("discover_title")}
                    </Typography.Title>
                    {discoverBooks.map((book) => <Book key={book.bookId} book={book} />)}
                </>
            ) : null}
        </Space>
    );
}

function Book(props) {
    const { t } = useTranslation();

    const [filled, setFilled] = useState(props.book.isMarked);
    const [userId] = useAtom(UserId);
    const [freezeActions, setFreezeActions] = useState(false);
    const [, setSearchBooks] = useAtom(SearchBooks);
    const [, setBookId] = useAtom(BookId);

    const navigate = useNavigate();

    const toggleMarked = (bookId, forceMark) => {
        setSearchBooks((prevState) => ({
            ...prevState,
            booksLite: prevState.booksLite?.map((book) =>
                book.bookId === bookId ? { ...book, isMarked: forceMark ?? !book.isMarked } : book
            )
        }));
    };

    const actionBookmark = MutateData(QueryId.ToggleUserBook, () => {
        setFreezeActions(false);
    }, [
        { case: QueryCase.Success, onCase: () => { setFilled(true); toggleMarked(props.book.bookId, true); } },
        { case: QueryCase.Error, onCase: () => { setFilled(false); toggleMarked(props.book.bookId, false); } }
    ]);

    const actionDelete = MutateData(QueryId.ToggleUserBook, () => {
        setFreezeActions(false);
    }, [
        { case: QueryCase.Success, onCase: () => { setFilled(false); toggleMarked(props.book.bookId, false); } },
        { case: QueryCase.Error, onCase: () => { setFilled(true); toggleMarked(props.book.bookId, true); } }
    ]);

    const onBookmark = () => {
        setFreezeActions(true);
        actionBookmark.mutate({
            UserId: userId,
            BookId: props.book.bookId
        });
    };

    const onDelete = () => {
        setFreezeActions(true);
        actionDelete.mutate({
            UserId: userId,
            BookId: props.book.bookId
        });
    };

    return (
        <div className="rounded-2xl border border-white/10 bg-custom-darkgrey/40 p-3">
            <Flex gap={16} align="start">
                <div className="relative">
                    {filled ? (
                        <Popconfirm
                            title={t("confirm_delete_book_title")}
                            description={t("confirm_delete_book_description")}
                            onConfirm={onDelete}
                            okText={t("yes")}
                            cancelText={t("no")}
                            okButtonProps={{ className: "!border-none hover:!bg-custom-orange" }}
                            cancelButtonProps={{ className: "hover:!text-custom-white" }}
                        >
                            <Button
                                disabled={freezeActions}
                                className="absolute left-[84px] top-1 z-10 !border-custom-red !bg-custom-red disabled:!bg-custom-red active:!bg-custom-red"
                                icon={<PushpinFilled />}
                                size="large"
                            />
                        </Popconfirm>
                    ) : (
                        <Button
                            disabled={freezeActions}
                            className="absolute left-[84px] top-1 z-10 bg-[#3b82f6aa] disabled:bg-[#3b82f6aa]"
                            onClick={() => {
                                setFilled(true);
                                toggleMarked(props.book.bookId, true);
                                onBookmark();
                            }}
                            icon={<PushpinOutlined />}
                            size="large"
                        />
                    )}
                    <ComponentBookIcon book={props.book} />
                </div>
                <div className="flex-1 text-custom-white">
                    <Typography.Text className="!text-custom-white">{props.book.title.length > 69 ? `${props.book.title.substring(0, 50)}...` : props.book.title}</Typography.Text>
                    <Typography.Paragraph className="!mb-1 !text-custom-white/90">
                        {Array.isArray(props.book.authors) ? props.book.authors.join(", ") : props.book.authors}
                    </Typography.Paragraph>
                    <Typography.Text className="!mb-3 !block !text-custom-white/75">
                        {t("page_amount")} {props.book.pageCount === 0 ? t("unknown") : props.book.pageCount}
                    </Typography.Text>
                    <Button
                        disabled={freezeActions}
                        className="min-w-[128px] hover:bg-[#3b82f6aa] disabled:bg-[#3b82f6aa]"
                        onClick={() => {
                            setBookId(props.book.bookId);
                            navigate(WebRoutes.Book);
                        }}
                        size="large"
                    >
                        {t("view_book")}
                    </Button>
                </div>
            </Flex>
        </div>
    );
}
